import React from "react";
import { Stack, Typography, Chip, Box } from "@mui/material";
import { TreeItem2, TreeItem2Props, treeItemClasses } from "@mui/x-tree-view";
import { styled, alpha } from "@mui/material/styles";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "../../services/mimshakService";

export interface TreeItemLabelData {
  primary: string;
  secondary: string;
  chip?: string;
  grayText?: string;
  colorHex?: string;
}

export const buildTreeItemLabelString = (data: TreeItemLabelData): string => {
  return JSON.stringify(data);
};

export const parseTreeItemLabel = (label: string): TreeItemLabelData => {
  try {
    return JSON.parse(label);
  } catch {
    return {
      primary: label,
      secondary: "",
    };
  }
};

export const buildTreeItemLabel = (
  primary: string,
  secondary: string,
  chip?: string,
  grayText?: string,
  colorHex?: string
): React.ReactNode => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        color: "black",
        direction: "rtl",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {colorHex && (
          <Box
            sx={{
              width: 22,
              minWidth: 22,
              height: 22,
              bgcolor: colorHex,
              borderRadius: 1.5,
              border: "1px solid #EEE",
              marginLeft: "10px !important",
            }}
          />
        )}
        <Typography sx={{ fontSize: "1.3rem", fontWeight: 500 }}>
          {primary}
        </Typography>
        {grayText && (
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "gray",
              marginRight: "10px !important",
            }}
          >
            {grayText}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
        {chip && <Chip label={chip} size="small" color="info" />}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
          
        //    direction: "ltr",
            textAlign: "right",
            minWidth: 90,
          }}
        >
          {secondary}
        </Typography>
      </Box>
    </Box>
  );
};

export const CustomCheckbox = React.forwardRef(function CustomCheckbox(
  props: React.InputHTMLAttributes<HTMLInputElement> & { ownerState?: any; visible?: boolean },
  ref: React.Ref<HTMLInputElement>
) {
  const { ownerState, visible, ...inputProps } = props;
  return <input type="checkbox" ref={ref} {...inputProps} />;
});

export const CustomTreeItem = styled(
    React.forwardRef(function CustomTreeItem(
      props: Omit<TreeItem2Props, 'itemId'> & {
        itemId: string;
        onLeafClick?: (itemId: string) => void;
        products?: Product[];
        setSelectedProduct?: (product: Product) => void;
        hideCheckbox?: boolean;
      },
      ref: React.Ref<HTMLLIElement>
    ) {
      const {
        label,
        itemId,
        onLeafClick,
        products,
        setSelectedProduct,
        hideCheckbox,
        ...rest
      } = props;

      const { primary, secondary, chip, grayText, colorHex } = parseTreeItemLabel(label as string);
      const isLeaf = !props.children;

      const handleClick = () => {
        if (itemId?.startsWith("policy-")) {
          const actualPathId = itemId.replace("policy-", "");
          let matchedProduct = MimshakService.getProductByPolicyNumber2(products,actualPathId)


          if (matchedProduct && setSelectedProduct) {
            setSelectedProduct(matchedProduct);
          }

          if (onLeafClick) {
            onLeafClick(itemId);
          }
        }
      };

      return (
        <TreeItem2
          {...rest}
          ref={ref}
          itemId={itemId}
          slots={hideCheckbox ? {} : { checkbox: CustomCheckbox }}
          label={buildTreeItemLabel(primary, secondary, chip, grayText, colorHex)}
          onClick={handleClick}
        />
      );
    })
)(({ theme }) => ({
  color: theme.palette.grey[200],
  direction: "rtl",
  [`& .${treeItemClasses.content}`]: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.2, 0),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [`& .${treeItemClasses.label}`]: {
      fontSize: "1.4rem",
      fontWeight: 500,
    },
  },
  [`& .${treeItemClasses.iconContainer}`]: {
      width: 28, // ✅ גודל קבוע של האייקון
      height: 28, // ✅ גודל קבוע של האייקון
      borderRadius: "50%", // ✅ צורה עגולה
    backgroundColor: theme.palette.primary.dark,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
      transform: "rotate(180deg)", // ✅ סיבוב החץ שמאלה
    ...theme.applyStyles("light", {
        color: theme.palette.text.primary, // לרוב שחור
      backgroundColor: alpha(theme.palette.primary.main, 0.25),
    }),
    ...theme.applyStyles("dark", {
      color: theme.palette.primary.contrastText,
    }),
    '&:empty': {
      display: 'none',
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginRight: 15,
    paddingRight: 18,
    borderRight: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));
